/**
 * Extract dominant color + dimensions from an image file.
 * Returns hex color (e.g., "#a3b2c1") and pixel dimensions.
 */
export async function extractDominantColor(
  file: File
): Promise<{ color: string; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No canvas context'));
        return;
      }

      // Downscale for speed
      const MAX = 64;
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      let r = 0, g = 0, b = 0, count = 0;
      try {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha < 128) continue; // skip transparent
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
      } catch (err) {
        reject(err);
        return;
      }

      if (count === 0) {
        resolve({ color: '#cccccc', width: img.width, height: img.height });
        return;
      }

      const avg = [
        Math.round(r / count),
        Math.round(g / count),
        Math.round(b / count),
      ];
      const hex = '#' + avg.map((c) => c.toString(16).padStart(2, '0')).join('');
      resolve({ color: hex, width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed'));
    };

    img.src = url;
  });
}

/**
 * Bake a translate/scale transform into a PNG by compositing the source onto a
 * 3:4 canvas. Mirrors the CSS rendering of `<img object-contain>` with
 * `transform: translate(x%, y%) scale(s)` so what the admin saw in the composer
 * matches what gets saved. Customer-side `coverDesignTransform` still applies
 * on top of the baked PNG (starting from identity).
 */
export async function bakeDesignToPng(
  source: File | string,
  transform: { x: number; y: number; scale: number },
  options?: { width?: number; height?: number }
): Promise<{ blob: Blob; width: number; height: number }> {
  const targetW = options?.width ?? 1500;
  const targetH = options?.height ?? 2000;

  return new Promise((resolve, reject) => {
    const blobUrl = typeof source === 'string' ? null : URL.createObjectURL(source);
    const imgSrc = blobUrl ?? (source as string);
    const img = new Image();
    // crossOrigin must be set before src so the canvas can read pixels back
    // from a remote URL (R2). For File-derived blob URLs this is a no-op.
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No canvas context'));
        return;
      }

      const imgAR = img.width / img.height;
      const canvasAR = targetW / targetH;
      let baseW: number;
      let baseH: number;
      if (imgAR > canvasAR) {
        baseW = targetW;
        baseH = targetW / imgAR;
      } else {
        baseH = targetH;
        baseW = targetH * imgAR;
      }

      const drawW = baseW * transform.scale;
      const drawH = baseH * transform.scale;
      const offsetX = (transform.x / 100) * targetW;
      const offsetY = (transform.y / 100) * targetH;
      const x = (targetW - drawW) / 2 + offsetX;
      const y = (targetH - drawH) / 2 + offsetY;

      try {
        ctx.drawImage(img, x, y, drawW, drawH);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
        return;
      }
      canvas.toBlob((blob) => {
        if (!blob) {
          // toBlob returns null when the canvas is tainted by a cross-origin
          // source — surface that specifically so callers can offer a re-upload.
          reject(new Error('Canvas tainted: source PNG did not return CORS headers'));
          return;
        }
        resolve({ blob, width: targetW, height: targetH });
      }, 'image/png');
    };
    img.onerror = () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      reject(new Error('Image load failed'));
    };
    img.src = imgSrc;
  });
}

/**
 * Load an image from URL and return its dimensions.
 */
export async function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}

/** Max bytes accepted for an admin-uploaded custom cover icon. */
export const MAX_COVER_UPLOAD_BYTES = 10 * 1024 * 1024;

/** True for image MIME types we can decode and re-encode to PNG via canvas. */
export function isConvertibleImageType(mime: string): boolean {
  return /^image\/(png|jpe?g|webp|gif|bmp)$/i.test(mime);
}

/** True when the file is an iPhone HEIC/HEIF, detected by MIME OR extension.
 *  iOS often hands the file through with an empty or `image/heic` MIME and a
 *  `.heic`/`.heif` name — checking both catches every case. */
export function isHeicFile(file: File): boolean {
  const mime = (file.type || '').toLowerCase();
  if (mime === 'image/heic' || mime === 'image/heif') return true;
  const name = (file.name || '').toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif');
}

/**
 * Normalise a picked cover/icon image so the rest of the upload path always
 * gets a browser-decodable file. The only transform is HEIC/HEIF → JPEG (via
 * heic2any, dynamically imported so it stays out of the main bundle); every
 * other format is returned untouched.
 *
 * Why this exists: iPhone photos are HEIC, and the cover uploaders used to
 * reject them outright ("Format non supporté"), so admins/customers on iOS
 * literally could not place their own photo on the cover. This mirrors the
 * HEIC handling the bulk photo-upload + PDF paths already do.
 */
export async function normalizeCoverImageFile(file: File): Promise<File> {
  if (!isHeicFile(file)) return file;
  const heic2any = (await import('heic2any')).default;
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  const blob = Array.isArray(out) ? out[0] : out;
  const baseName = (file.name || 'cover').replace(/\.(heic|heif)$/i, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

/**
 * Decode any browser-supported image (PNG/JPEG/WebP/…) and re-encode it to PNG,
 * preserving transparency, via a canvas. Downscales so the long edge is
 * <= maxEdge to keep the embedded PDF light. `pdf-lib` can only embed PNG/JPEG,
 * so converting here is what makes a WebP/etc upload show up in the exported PDF.
 *
 * Rejects on decode failure or a tainted canvas (cross-origin source).
 */
export async function convertImageFileToPng(
  file: File,
  opts?: { maxEdge?: number }
): Promise<Blob> {
  const maxEdge = opts?.maxEdge ?? 2000;
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('PNG encode failed'));
          return;
        }
        resolve(blob);
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed'));
    };
    img.src = url;
  });
}
